import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, finalize, noop, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import { TsSnackbarService } from 'tsui';
import { AccountService } from 'app/core';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { TableSortState } from '@shared/models';
import { Project, Property } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { FieldDef, QueryConfig, ProjectPhase, ProjectStage, SavedView } from 'app/core/models';
import { PropertyDealAddComponent } from 'app/modules/properties/modals/deal-add/deal-add.component';
import { generateDownloadableFile, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';
import { ProjectStatusRequiredFieldsComponent } from 'app/modules/projects/modals/project-status-required-fields/project-status-required-fields.component';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'property-deals',
  templateUrl: './deals.component.html',
  styleUrls: ['./deals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDealsComponent {
  title: string = 'Related Deals';
  saved_view_name: string = 'Property Deals';
  deals_related_to_type: string = 'PropertyDeals';
  default_page_size = 20;
  default_page = 0;
  data: Data;
  property: Property;
  savedView: SavedView;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  queryFilters: Partial<QueryConfig.QueryFilters> = {};
  pageEvent: PageEvent;
  sortState: TableSortState;
  results_dataset: any;
  results_pagination: any;
  projectStages: ProjectStage[];
  projectPhases: ProjectPhase[];
  isLoading: boolean = false;
  ready = new BehaviorSubject(false);

  private defaultDefinition = {
    table: {
      fields: [
        'title',
        'project_stage_id',
        'project_phase_id',
        'annual_rent',
        'city',
        'rentable_square_feet',
        'occupancy',
        'bid_price',
        'price_per_sq_ft',
        'noi',
      ],
      sort: { active: 'project_stage_id', direction: 'asc' },
    },
  };

  get fieldDefs(): FieldDef[] {
    return this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly projectsService: ProjectsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly matDialog: MatDialog,
    private readonly snackBar: TsSnackbarService,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data;
      this.property = data.property;
      this.projectStages = this.data.options.project_stages.filter((option) => !option.hidden);
      this.projectPhases = this.data.options.project_phases.filter((option) => !option.hidden);

      // If savedView named 'Property Deals' doesn't exist, create it.
      if (!this.data.savedViews.filter((view: SavedView) => view.name === this.saved_view_name)[0]) {
        const existingProjectView: SavedView = this.data.savedViews.filter(
          (view: SavedView) => view.related_to_type === RelatedToType.PropertyDeals,
        )[0];

        const dealsSavedView = {
          id: null,
          name: this.saved_view_name,
          related_to_type: this.deals_related_to_type,
          definition: existingProjectView?.definition ?? JSON.stringify(this.defaultDefinition),
          account_id: +this.store.selectSnapshot(AccountState.getAccount).id,
        };

        this.accountService
          .createSavedView(dealsSavedView)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response) => {
            this.savedView = response;
            this.sortState = this.getSortState();
            this.setColumnsAndPageEvent();
          });
      } else {
        this.savedView = this.data.savedViews.filter((view: SavedView) => view.name === this.saved_view_name)[0];
        this.sortState = this.getSortState();
        this.setColumnsAndPageEvent();
      }
    });
  }

  private getSortState(): TableSortState {
    const savedSort = JSON.parse(this.savedView?.definition)?.table?.sort;

    return {
      active: savedSort?.active || 'title',
      direction: savedSort?.direction || 'asc',
    };
  }

  private setDisplayColumns(): void {
    if (this.savedView) {
      this.displayColumns = removeArchivedFieldsFromTableDefinition(
        this.savedView ? JSON.parse(this.savedView.definition).table.fields : ['title', 'created_at'],
        this.fieldDefs,
      );
      this.loopDisplayColumns = [...this.displayColumns];
    }
  }

  private setColumnsAndPageEvent(): void {
    this.setDisplayColumns();

    this.pageEvent = new PageEvent();
    this.pageEvent.pageIndex = this.default_page;
    this.pageEvent.pageSize = this.default_page_size;

    this.ready.next(true);

    this.getProjects();
  }

  page(event: MatPaginator): void {
    this.pageEvent.pageSize = event.pageSize;
    this.pageEvent.pageIndex = event.pageIndex;
    this.getProjects();
  }

  sort(event: TableSortState): void {
    if (event.active === 'name') this.sortState.active = 'title';
    else this.sortState.active = event.active;
    this.sortState.direction = event.direction;
    this.getProjects();
  }

  search(event: Partial<QueryConfig.QueryFilters>): void {
    this.pageEvent.pageIndex = 0;
    this.cleanAndRequestProperties(event);
  }

  filter(event: Partial<QueryConfig.QueryFilters>): void {
    this.pageEvent.pageIndex = 0;
    this.cleanAndRequestProperties(event);
  }

  onDealAdded(mode: 'add' | 'link'): void {
    this.matDialog
      .open(PropertyDealAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        width: '40vw',
        disableClose: true,
        data: { data: this.data, selectedProperties: { selected: [this.property] }, dealType: 'single', mode },
      })
      .afterClosed()
      .pipe(
        tap((result) => {
          this.handleDealLink(mode, result);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(noop);
  }

  private handleDealLink(mode: 'add' | 'link', result) {
    if (mode === 'link' && result?.linked_project_id) {
      this.property.project_ids.push(result.linked_project_id);
      this.getProjects();
    }
  }

  update(event: any): void {
    this.isLoading = true;
    const { params, item } = event;
    const options = { ...params, id: item.id };
    const selectColumn = [];
    const paramsKeys = Object.keys(params);
    const [firstParamsKey] = paramsKeys;

    selectColumn.push(firstParamsKey);

    if (firstParamsKey === 'add_new') {
      this.getProjects();
      this.isLoading = false;
    } else {
      this.projectsService
        .saveProject(options, item)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.isLoading = false;
            this.changeDetectorRef.markForCheck();
          }),
        )
        .subscribe(noop);

      if (paramsKeys.includes('project_stage_id')) {
        const stage = this.projectStages.find((option: ProjectStage) => option.id === Number(params.project_stage_id));
        this._handleSoftRequiredFields(stage, item.id);
      } else if (paramsKeys.includes('project_phase_id')) {
        const phase = this.projectPhases.find(({ id }: ProjectPhase) => id === Number(params.project_phase_id));
        this._handleSoftRequiredFields(phase, item.id, false);
      }
    }
  }

  cleanAndRequestProperties(event: Partial<QueryConfig.QueryFilters>): void {
    this.queryFilters = {
      ...this.queryFilters,
      fjson: event?.fjson || '[]',
      q: event?.q || '',
    };

    this.getProjects();
  }

  private getCustomQueryFilters(): Partial<QueryConfig.QueryFilters> {
    const customQueryFilters = cloneDeep(this.queryFilters);

    if (this.queryFilters?.fjson?.length) {
      const customFjson = JSON.parse(customQueryFilters.fjson);
      customFjson.push(['id', 'in', [...(this.property?.project_ids || [])]]);
      customQueryFilters.fjson = JSON.stringify(customFjson);
    } else {
      customQueryFilters.fjson = JSON.stringify([['id', 'in', [...(this.property?.project_ids || [])]]]);
    }
    return customQueryFilters;
  }

  private getProjects(): void {
    const customQueryFilters = this.getCustomQueryFilters();

    this.projectsService
      .getProjectsNoPipeline(
        this.loopDisplayColumns.concat(['pipeline_id']),
        customQueryFilters,
        this.pageEvent.pageIndex,
        this.pageEvent.pageSize,
        this.sortState.active,
        this.sortState.direction,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        this.results_dataset = results.projects;
        this.results_pagination = results.pagination;
        this.changeDetectorRef.markForCheck();
      });
  }

  private _handleSoftRequiredFields(entity: ProjectStage | ProjectPhase, projectId: string, isStatus = true): void {
    if (entity?.required_fields) {
      const project = this.results_dataset.find(({ id }: Project) => id === projectId);
      this.matDialog.open(ProjectStatusRequiredFieldsComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { data: this.data, stage: entity, project, isStatus },
      });
    }
  }

  exportCsv(): void {
    const customQueryFilters = this.getCustomQueryFilters();

    this.projectsService
      .getProjectsNoPipeline(
        this.loopDisplayColumns.concat(['pipeline_id']),
        customQueryFilters,
        this.pageEvent.pageIndex,
        this.pageEvent.pageSize,
        this.sortState.active,
        this.sortState.direction,
        this.queryFilters.q,
        false,
        FileFormat.CSV,
        false,
        this.savedView.id,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.message) {
          this.snackBar.open(data?.message);
        } else {
          generateDownloadableFile(data, FileFormat.CSV, `${this.property.name}'s deals`);
        }
      });
  }
}
